import React, { useState, useEffect } from 'react';
import { getData } from '../api/ApiServices';

interface InwardData {
  id: string;
  partId:{
    part:{
      internalPartNo: string;
      partName: string;
    };
  } 
  qty: number;
  inwardDate: string;
  lotNo: string;
  poId?: {
    poNo: string;
  };
  batchNo?: string;
}

interface BarcodeData {
  id: string;
  tagNumber: string;
  qty: number;
  balanceQty: number;
}

interface PartDetails {
  internalPartNo: string;
  partName: string;
}

interface ClosingStockPopupProps {
  isOpen: boolean;
  onClose: () => void;
  partId: string;
  startDate?: string;
  endDate?: string;
  isStockSummary: boolean;
}

const customPopupStyles = `
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .popup-content {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 1000px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
  
  .popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .popup-title {
    font-size: 18px;
    font-weight: 600;
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #666;
  }
  
  .close-button:hover {
    color: #333;
  }
  
  .inward-details-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 15px;
  }
  
  .inward-details-table th,
  .inward-details-table td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }
  
  .inward-details-table th {
    background-color: #f5f5f5;
    font-weight: 600;
  }
  
  .inward-details-table tr:hover {
    background-color: #f9f9f9;
  }
  
  .right-align {
    text-align: right;
  }
  
  .loader {
    text-align: center;
    padding: 20px;
  }
  
  .error-message {
    color: #d32f2f;
    padding: 15px;
    background-color: #ffebee;
    border-radius: 4px;
    margin-bottom: 15px;
  }
  
  .no-data {
    text-align: center;
    padding: 30px;
    color: #666;
  }
`;


const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
};

const cleanDateString = (str?: string): string | undefined =>
  str?.split('?')[0];

const DetailsPopUp: React.FC<ClosingStockPopupProps> = ({
  isOpen,
  onClose,
  partId,
  startDate,
  endDate,
  isStockSummary,
}) => {
  const [inwardData, setInwardData] = useState<InwardData[]>([]);
  const [barcodeData, setBarcodeData] = useState<BarcodeData[]>([]);
  const [partDetails, setPartDetails] = useState<PartDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !partId) return;
    isStockSummary ? fetchInward() : fetchBarcodes();
  }, [isOpen, partId, startDate, endDate]);

  // const dateFormatDDMMYYYY = (dateString: string) => {
  //   const date = new Date(dateString);
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const day = String(date.getDate()).padStart(2, "0");
  //   return `${day}-${month}-${year}`;
  // };
  

  // const cleanDateString = (dateStr?: string): string | undefined => {
  //   if (!dateStr) return undefined;
  //   return dateStr.split('?')[0];
  // };
  
  const fetchInward = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: 1,
        limit: 10,
        startdate: cleanDateString(startDate),
        enddate: cleanDateString(endDate),
        filter: {
         "partId.part._id":partId,
        }
      };
      const response: any = await getData(`/inwardoptions`, params);
      const data = response?.data?.data || [];
      setInwardData(data);
      if (data.length) {
        setPartDetails({
          internalPartNo: data[0].partId.part.internalPartNo,
          partName: data[0].partId.part.partName,
        });
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load inward details.');
    } finally {
      setIsLoading(false);
    }
  };


  const fetchBarcodes = async () => {
    setIsLoading(true);
    try {
      const response: any = await getData(`/inwardoptions/getBarcodes/${partId}`);
      const data = response?.data?.data || [];
      setBarcodeData(data);
      if (data.length && data[0].partId) {
        setPartDetails({
          internalPartNo: data[0].partId.part.internalPartNo,
          partName: data[0].partId.part.partName,
        });
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load barcode details.');
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;


  return (
    <div className="popup-overlay" onClick={onClose}>
      <style>{customPopupStyles}</style>
      <div className="popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-title">
            {partDetails
              ? `Details for ${partDetails.internalPartNo} - ${partDetails.partName}`
              : 'Details'}
            {startDate && endDate && isStockSummary && (
              <div style={{ fontSize: '14px', color: '#666' }}>
                Period: {formatDate(cleanDateString(startDate)!)} to {formatDate(cleanDateString(endDate)!)}
              </div>
            )}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {isLoading ? (
        <div className="loader">Loading inward details...</div>
          ) : isStockSummary ? (
            inwardData.length === 0 ? (
              <div className="no-data">No inward records found for this part.</div>
            ) : (
              <table className="inward-details-table">
                <thead>
                  <tr>
                    <th>Part No</th>
                    <th>Part Name</th>
                    <th className="right-align">Inward Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {inwardData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.partId.part.internalPartNo}</td>
                      <td>{item.partId.part.partName || 'N/A'}</td>
                      <td className="right-align">{item.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            barcodeData.length === 0 ? (
              <div className="no-data">No barcode records found for this part.</div>
            ) : (
              <table className="inward-details-table">
                <thead>
                  <tr>
                    <th>Tag Number</th>
                    <th className="right-align">Quantity</th>
                    <th className="right-align">Balance Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {barcodeData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.tagNumber}</td>
                      <td className="right-align">{item.qty}</td>
                      <td className="right-align">{item.balanceQty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
      </div>
    </div>
  );
};

export default DetailsPopUp;
